/**
 * Shared Graph Renderer Utility
 * 
 * Provides reusable graph visualization rendering that can be used by
 * Graph App, Life Graph, and other graph-based processes.
 */

import type { GraphNode, GraphEdge } from './types.js';
import { GraphEngine } from './graph-engine.js';
import { calculateLayout, fitToBounds, type GraphNode as LayoutNode, type GraphEdge as LayoutEdge } from '../../core/graph-layout.js';
import { designTokens } from '../../core/design-system.js';

export interface GraphRendererOptions {
  width?: number;
  height?: number;
  iterations?: number;
  repulsionStrength?: number;
  attractionStrength?: number;
  minDistance?: number;
  clusterDistance?: number;
  nodeRadius?: number;
  categoryColors?: Record<string, string>;
  onNodeClick?: (nodeId: string) => void;
  onNodeHover?: (nodeId: string | null) => void;
  selectedNodeId?: string | null;
  highlightedNodeIds?: Set<string>;
}

const DEFAULT_CATEGORY_COLORS: Record<string, string> = {
  work: '#66ccff',
  fitness: '#4ade80',
  personal: '#fbbf24',
  health: '#f87171',
  finance: '#a78bfa',
  learning: '#60a5fa',
  default: '#94a3b8'
};

/**
 * Render a force-directed graph visualization
 */
export function renderGraphView(
  contentNodes: GraphNode[],
  graphEngine: GraphEngine,
  options: GraphRendererOptions = {}
): string {
  const opts = {
    width: 700,
    height: 400,
    iterations: 150,
    repulsionStrength: 2000,
    attractionStrength: 0.15,
    minDistance: 60,
    clusterDistance: 120,
    nodeRadius: 25,
    categoryColors: DEFAULT_CATEGORY_COLORS,
    ...options
  };

  // Convert GraphApp nodes to layout nodes
  const layoutNodes: LayoutNode[] = contentNodes.map(node => ({
    id: node.id,
    label: getNodeData(node, 'title') || getNodeData(node, 'label') || 'Node',
    radius: opts.nodeRadius,
    category: node.data.category as string || node.data.domain as string || 'default'
  }));

  // Get edges from graph engine
  const edges = graphEngine.getAllEdges().filter(edge => {
    // Only include edges between content nodes (not membership edges)
    return edge.type !== 'membership';
  });

  const layoutEdges: LayoutEdge[] = edges.map(edge => ({
    id: edge.id,
    from: edge.from,
    to: edge.to,
    weight: edge.type === 'temporal' ? 1.5 : 1.0
  }));

  // Calculate layout
  const layout = calculateLayout(
    { nodes: layoutNodes, edges: layoutEdges },
    {
      width: opts.width,
      height: opts.height,
      iterations: opts.iterations,
      repulsionStrength: opts.repulsionStrength,
      attractionStrength: opts.attractionStrength,
      minDistance: opts.minDistance,
      clusterDistance: opts.clusterDistance,
      nodeRadius: opts.nodeRadius
    }
  );

  // Fit to bounds
  const fittedLayout = fitToBounds(layout, opts.width, opts.height, 40);

  // Render SVG
  const svgNodes = fittedLayout.nodes.map(node => {
    const category = node.category || 'default';
    const color = opts.categoryColors[category] || opts.categoryColors.default;
    const isSelected = opts.selectedNodeId === node.id;
    const isHighlighted = opts.highlightedNodeIds?.has(node.id) || false;
    const radius = isSelected ? (opts.nodeRadius! + 8) : opts.nodeRadius!;
    const opacity = isSelected || isHighlighted ? 1.0 : 0.8;
    const strokeWidth = isSelected ? 3 : 2;
    
    const clickHandler = opts.onNodeClick 
      ? `onclick="window.__graphNodeClick && window.__graphNodeClick('${escapeHtml(node.id)}');"`
      : '';
    
    return `
      <g class="graph-node" data-node-id="${escapeHtml(node.id)}" ${clickHandler}>
        <circle
          cx="${node.x || 0}"
          cy="${node.y || 0}"
          r="${radius}"
          fill="${color}"
          fill-opacity="${opacity}"
          stroke="${isSelected ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)'}"
          stroke-width="${strokeWidth}"
          style="cursor: pointer; transition: r 0.2s ease, fill-opacity 0.2s ease;"
          onmouseover="this.setAttribute('r', '${radius + 5}'); this.style.fillOpacity='1'; window.__graphNodeHover && window.__graphNodeHover('${escapeHtml(node.id)}');"
          onmouseout="this.setAttribute('r', '${radius}'); this.style.fillOpacity='${opacity}'; window.__graphNodeHover && window.__graphNodeHover(null);"
        />
        <text
          x="${node.x || 0}"
          y="${(node.y || 0) + radius + 15}"
          text-anchor="middle"
          fill="rgba(255,255,255,0.9)"
          font-size="11"
          font-weight="${isSelected ? '600' : '500'}"
          pointer-events="none"
        >${escapeHtml(node.label || 'Node')}</text>
      </g>
    `;
  }).join('');

  const svgEdges = fittedLayout.edges.map(edge => {
    const fromNode = fittedLayout.nodes.find(n => n.id === edge.from);
    const toNode = fittedLayout.nodes.find(n => n.id === edge.to);

    if (!fromNode || !toNode || fromNode.x === undefined || fromNode.y === undefined ||
        toNode.x === undefined || toNode.y === undefined) {
      return '';
    }

    const isHighlighted = opts.highlightedNodeIds?.has(edge.from) || 
                         opts.highlightedNodeIds?.has(edge.to) ||
                         opts.selectedNodeId === edge.from ||
                         opts.selectedNodeId === edge.to;

    return `
      <line
        x1="${fromNode.x}"
        y1="${fromNode.y}"
        x2="${toNode.x}"
        y2="${toNode.y}"
        stroke="rgba(255,255,255,${isHighlighted ? '0.4' : '0.2'})"
        stroke-width="${isHighlighted ? '2' : '1.5'}"
        stroke-opacity="${isHighlighted ? '0.6' : '0.4'}"
      />
    `;
  }).join('');

  return `
    <svg width="${opts.width}" height="${opts.height}" style="display: block; margin: 0 auto;">
      <g>
        ${svgEdges}
        ${svgNodes}
      </g>
    </svg>
  `;
}

/**
 * Helper to get node data with fallbacks
 */
function getNodeData(node: GraphNode, field: string, fallback: string = ''): string {
  return (node.metadata?.[field] as string) ||
         (node.data[field] as string) ||
         fallback;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

